import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner } from 'react-bootstrap';
import DatepickerRange from "../molecules/DatepickerRange";
import PreviewResults from "../molecules/PreviewResults";
import moment from "moment";
import axios from "axios";

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const AdminSettings = () => {
    const [branches, setBranches] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState('');
    const [startDate, setStartDate] = useState(moment().subtract(1, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingBranches, setIsLoadingBranches] = useState(false);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // success, warning, danger
    const [previewData, setPreviewData] = useState(null);
    const [showPreview, setShowPreview] = useState(false);

    // Load branches on component mount
    useEffect(() => {
        loadBranches();
    }, []);

    const loadBranches = async () => {
        setIsLoadingBranches(true);
        try {
            // Use the same pattern as working ReportManagement (rely on axios defaults)
            const response = await axios.get(`${API_BASE_URL}/admin-settings/branches`);

            if (response.data.success) {
                setBranches(response.data.branches);
            } else {
                setMessage('브랜치 목록을 불러오는데 실패했습니다.');
                setMessageType('warning');
            }
        } catch (error) {
            console.error('Load branches error:', error);
            setMessage('브랜치 목록을 불러오는데 실패했습니다.');
            setMessageType('danger');
        } finally {
            setIsLoadingBranches(false);
        }
    };

    const handleDateRangeChange = (dates) => {
        if (dates && dates.length === 2) {
            setStartDate(dates[0] || '');
            setEndDate(dates[1] || '');
            // Reset preview when dates change
            setShowPreview(false);
            setPreviewData(null);
            setMessage('');
        }
    };

    const validateDateRange = () => {
        if (!startDate || !endDate) {
            setMessage('날짜 범위를 선택해주세요.');
            setMessageType('warning');
            return false;
        }

        const start = moment(startDate);
        const end = moment(endDate);
        const diffDays = end.diff(start, 'days') + 1; // Include both start and end date

        if (diffDays > 3) {
            setMessage(`⚠️ 날짜 범위는 최대 3일까지 선택 가능합니다. (현재: ${diffDays}일)`);
            setMessageType('warning');
            return false;
        }

        return true;
    };


    const handlePreview = async () => {
        if (!selectedBranch) {
            setMessage('브랜치를 선택해주세요.');
            setMessageType('warning');
            return;
        }

        if (!validateDateRange()) return;

        setIsLoadingPreview(true);
        setMessage('미리보기를 불러오는 중...');
        setMessageType('info');

        try {
            // Use the same pattern as working components (rely on axios defaults)
            const response = await axios.get(`${API_BASE_URL}/admin-settings/preview`, {
                params: {
                    branchId: selectedBranch,
                    startDate,
                    endDate
                },
                timeout: 1200000 // 1200 seconds (20 minutes, same as backend)
            });

            if (response.data.success) {
                setPreviewData(response.data);
                setShowPreview(true);
                setMessage('');
                setMessageType('');
            } else {
                setMessage(`❌ 미리보기 실패: ${response.data.error}`);
                setMessageType('danger');
            }

        } catch (error) {
            console.error('Preview error:', error);
            let errorMessage = '미리보기 중 오류가 발생했습니다.';
            
            if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.code === 'ECONNABORTED') {
                errorMessage = '요청 시간이 초과되었습니다.';
            }
            
            setMessage(`❌ ${errorMessage}`);
            setMessageType('danger');
        } finally {
            setIsLoadingPreview(false);
        }
    };

    const handleConfirmRepull = async () => {
        if (!previewData?.summary?.actionRequired) {
            setMessage('실행할 작업이 없습니다.');
            setMessageType('info');
            return;
        }

        setIsLoading(true);
        setMessage('동기화 작업을 시작합니다...');
        setMessageType('info');

        try {
            // Use the same pattern as working components (rely on axios defaults)
            const response = await axios.post(`${API_BASE_URL}/admin-settings/sync`, {
                branchId: selectedBranch,
                startDate,
                endDate
            }, {
                timeout: 1200000 // 1200 seconds (20 minutes)
            });

            if (response.data.success) {
                const { branch, results } = response.data;
                let message = `✅ 동기화 작업이 완료되었습니다!\n\n` +
                    `📍 브랜치: ${branch.name}\n` +
                    `📅 기간: ${startDate} ~ ${endDate}\n\n` +
                    `👥 사용자: ${results.users.created}명 생성, ${results.users.skipped}명 스킵\n` +
                    `📊 리포트: ${results.reports.created}개 생성, ${results.reports.updated}개 업데이트, ${results.reports.skipped}개 스킵\n`;

                // Add warning for reports skipped due to missing users
                if (results.reports.skippedDueToMissingUser > 0) {
                    message += `\n⚠️ 주의: ${results.reports.skippedDueToMissingUser}개 리포트는 사용자가 없어서 스킵되었습니다.\n` +
                        `   → 먼저 "사용자 동기화"를 실행한 후 다시 시도하세요.\n`;
                }
                
                // Add PDF results if available
                if (results.pdfs && results.pdfs.total > 0) {
                    message += `📄 PDF: ${results.pdfs.regenerated}/${results.pdfs.total}개 생성 시작\n`;
                    if (results.pdfs.regenerated > 0) {
                        message += `   → PDF 생성이 백그라운드에서 진행 중입니다.\n`;
                        message += `   → 5-10분 후 완성될 예정입니다.\n`;
                    }
                }
                
                message += `\n⏳ 이미지 다운로드 및 PDF 생성은 백그라운드에서 진행됩니다.\n` +
                    `완료까지 5-20분 소요될 수 있습니다.`;
                
                setMessage(message);
                setMessageType('success');
                // Clear preview data after successful sync
                setShowPreview(false);
                setPreviewData(null);
            } else {
                setMessage(`❌ 동기화 실패: ${response.data.message}`);
                setMessageType('danger');
            }

        } catch (error) {
            console.error('Sync error:', error);
            let errorMessage = '동기화 작업 중 오류가 발생했습니다.';
            
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.code === 'ECONNABORTED') {
                errorMessage = '요청 시간이 초과되었습니다. 백그라운드에서 계속 진행될 수 있습니다.';
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            setMessage(`❌ ${errorMessage}`);
            setMessageType('danger');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelPreview = () => {
        setShowPreview(false);
        setPreviewData(null);
        setMessage('');
        setMessageType('');
    };


    return (
        <Container fluid className="p-4">
            <Row>
                <Col>
                    <Card>
                        <Card.Header>
                            <h4 className="mb-0">🔧 관리자 설정</h4>
                            <small className="text-muted">SuperAdmin 전용 - Evelab 데이터 동기화 시스템</small>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label><strong>브랜치 선택</strong></Form.Label>
                                        <Form.Select 
                                            value={selectedBranch} 
                                            onChange={(e) => setSelectedBranch(e.target.value)}
                                            disabled={isLoading || isLoadingBranches}
                                        >
                                            <option value="">
                                                {isLoadingBranches ? '브랜치 목록 로딩 중...' : '브랜치를 선택하세요'}
                                            </option>
                                            {branches.map((branch) => (
                                                <option key={branch.id} value={branch.id}>
                                                    {branch.name} (ID: {branch.branchId})
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label><strong>동기화 날짜 범위</strong></Form.Label>
                                        <DatepickerRange
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={handleDateRangeChange}
                                            placeholder="날짜를 선택하세요"
                                        />
                                        <Form.Text className="text-muted">
                                            선택한 날짜의 오전 5시 ~ 오후 9시 (한국시간) 데이터를 동기화합니다.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {selectedBranch && startDate && endDate && (
                                <Row className="mb-3">
                                    <Col>
                                        <Alert variant="info">
                                            <strong>선택된 브랜치:</strong> {branches.find(b => b.id === selectedBranch)?.name}<br/>
                                            <strong>동기화 기간:</strong> {startDate} ~ {endDate}<br/>
                                            <strong>처리 시간:</strong> 한국시간 {startDate} 05:00 ~ {endDate} 21:00
                                        </Alert>
                                    </Col>
                                </Row>
                            )}

                            {!showPreview && (
                                <Row className="mb-3">
                                    <Col>
                                        <Button 
                                            variant="info" 
                                            size="lg"
                                            onClick={handlePreview}
                                            disabled={!selectedBranch || !startDate || !endDate || isLoadingPreview}
                                            className="w-100"
                                        >
                                            {isLoadingPreview ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    미리보기 로딩 중...
                                                </>
                                            ) : (
                                                '🔍 동기화 미리보기'
                                            )}
                                        </Button>
                                    </Col>
                                </Row>
                            )}
                            
                            {showPreview && (
                                <PreviewResults 
                                    previewData={previewData}
                                    onConfirm={handleConfirmRepull}
                                    onCancel={handleCancelPreview}
                                    isLoading={isLoading}
                                />
                            )}

                            {message && (
                                <Row>
                                    <Col>
                                        <Alert variant={messageType} style={{ whiteSpace: 'pre-line' }}>
                                            {message}
                                        </Alert>
                                    </Col>
                                </Row>
                            )}

                            <Row>
                                <Col>
                                    <Card className="bg-light">
                                        <Card.Body>
                                            <h6>📋 동기화 프로세스</h6>
                                            <ol className="mb-0">
                                                <li><strong>사용자 동기화:</strong> Evelab에서 누락된 사용자 정보 가져오기</li>
                                                <li><strong>리포트 동기화:</strong> 해당 기간의 리포트 및 이미지 다운로드</li>
                                                <li><strong>백그라운드 처리:</strong> 이미지 다운로드는 백그라운드에서 진행</li>
                                                <li><strong>완료 확인:</strong> 처리 완료까지 3-10분 소요 가능</li>
                                            </ol>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default AdminSettings;